<template><div><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The resizable component splits a container into adjustable panels separated by draggable handles. It works well for sidebar layouts, split-view editors, and any interface where users should control how space is distributed.</p>
<code-example only-show="code" source-code='&lt;dt-resizable&gt;
  &lt;dt-resizable-panel&gt; ... &lt;/dt-resizable-panel&gt;
  &lt;dt-resizable-handle /&gt;
  &lt;dt-resizable-panel&gt; ... &lt;/dt-resizable-panel&gt;
&lt;/dt-resizable&gt;'>
<dt-resizable>
  <dt-resizable-panel> ... </dt-resizable-panel>
  <dt-resizable-handle />
  <dt-resizable-panel> ... </dt-resizable-panel>
</dt-resizable>
</code-example>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="two-panels" tabindex="-1" class="d-docsite--header-3">Two panels <a class="header-anchor d-link d-docsite--link d-link" href="#two-panels" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel id=&quot;sidebar&quot; initial-size=&quot;25p&quot;&gt;
      Sidebar
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;content&quot;&gt;
      Main content
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<example-resizable />
</code-example>
<h3 id="three-panels" tabindex="-1" class="d-docsite--header-3">Three panels <a class="header-anchor d-link d-docsite--link d-link" href="#three-panels" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel id=&quot;ex3-sidebar&quot; initial-size=&quot;20p&quot; user-min-size=&quot;300&quot;&gt;
      Sidebar
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;ex3-content&quot;&gt;
      Content
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;ex3-details&quot; initial-size=&quot;25p&quot; user-min-size=&quot;300&quot;&gt;
      Details
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<example-resizable-three-panel />
</code-example>
<h3 id="vertical" tabindex="-1" class="d-docsite--header-3">Vertical <a class="header-anchor d-link d-docsite--link d-link" href="#vertical" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable direction=&quot;column&quot;&gt;
    &lt;dt-resizable-panel id=&quot;exv-top&quot; initial-size=&quot;40p&quot;&gt;
      Top Panel
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;exv-bottom&quot;&gt;
      Bottom Panel
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<example-resizable-vertical />
</code-example>
<h3 id="nested-layouts" tabindex="-1" class="d-docsite--header-3">Nested layouts <a class="header-anchor d-link d-docsite--link d-link" href="#nested-layouts" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Resizable groups can be nested. For example, a horizontal sidebar + content layout where the content area is itself a vertical split:</p>
<code-example source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel id=&quot;ex-sidebar&quot; initial-size=&quot;25p&quot; user-min-size=&quot;300&quot; user-max-size=&quot;50p&quot;&gt;
      Sidebar
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;content&quot;&gt;
      &lt;dt-resizable direction=&quot;column&quot;&gt;
        &lt;dt-resizable-panel id=&quot;editor&quot; initial-size=&quot;25p&quot;&gt;
          Content Start
        &lt;/dt-resizable-panel&gt;
        &lt;dt-resizable-handle /&gt;
        &lt;dt-resizable-panel id=&quot;terminal&quot;&gt;
          Content End
        &lt;/dt-resizable-panel&gt;
      &lt;/dt-resizable&gt;
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<example-resizable-nested />
</code-example>
<h2 id="best-practices" tabindex="-1" class="d-docsite--header-2">Best practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h2>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Set <code v-pre>initial-size</code> on panels with a known width (sidebars, detail panes). Omit it on the main content panel so it fills the remaining space.</li>
<li class="d-docsite--list-element">Set <code v-pre>user-min-size</code> on every panel. Without it, panels can shrink to nearly zero. A minimum of <code v-pre>&quot;300&quot;</code> (192px) keeps most content usable.</li>
<li class="d-docsite--list-element">Use <code v-pre>storage-key</code> to persist layouts. Users expect their panel arrangement to survive a page refresh.</li>
<li class="d-docsite--list-element">Always provide a way to restore collapsed panels — for example, a menu icon button in a sibling panel's header.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't pass raw pixel values for sizes. Only percentage tokens (<code v-pre>&quot;25p&quot;</code>) and Dialtone layout tokens (<code v-pre>&quot;500&quot;</code>) are accepted.</li>
<li class="d-docsite--list-element">Don't set <code v-pre>initial-size</code> on every panel. Leave one panel without it so it absorbs remaining space and the layout always fills the container.</li>
<li class="d-docsite--list-element">Don't hide a panel without giving the user a way to bring it back. Provide an expand control in a visible sibling panel (e.g., a menu button in the content header).</li>
</ul>
</template>
</dialtone-usage>
<h2 id="constraints" tabindex="-1" class="d-docsite--header-2">Constraints <a class="header-anchor d-link d-docsite--link d-link" href="#constraints" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">All size props accept two formats: percentage tokens (e.g., <code v-pre>&quot;25p&quot;</code> for 25% of the container) and Dialtone layout tokens (e.g., <code v-pre>&quot;500&quot;</code> which resolves to 320px). Raw pixel values are not accepted — the component resolves token values from Dialtone's <code v-pre>--dt-layout-*</code> CSS custom properties at runtime, matching DtBox and the <code v-pre>d-w-*</code> / <code v-pre>d-h-*</code> utility classes.</p>
<p class="d-docsite--paragraph"><code v-pre>initial-size</code> defines where a panel starts. For panels whose size should flex with the available space (like a main content area), omit <code v-pre>initial-size</code> and the panel will fill whatever space remains.</p>
<h3 id="user-constraints" tabindex="-1" class="d-docsite--header-3">User constraints <a class="header-anchor d-link d-docsite--link d-link" href="#user-constraints" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>user-min-size</code> and <code v-pre>user-max-size</code> set hard limits on how small or large a user can drag a panel. These are enforced during drag interactions.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel
      id=&quot;sidebar&quot;
      initial-size=&quot;30p&quot;
      user-min-size=&quot;20p&quot;
      user-max-size=&quot;50p&quot;
    &gt;
      Sidebar (min 20%, max 50%)
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable>
    <dt-resizable-panel
      id="sidebar"
      initial-size="30p"
      user-min-size="20p"
      user-max-size="50p"
    >
      Sidebar (min 20%, max 50%)
    </dt-resizable-panel>
  </dt-resizable>
</dt-box>
</code-example>
<h3 id="system-constraints" tabindex="-1" class="d-docsite--header-3">System constraints <a class="header-anchor d-link d-docsite--link d-link" href="#system-constraints" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>system-min-size</code> and <code v-pre>system-max-size</code> define the range the layout engine uses when redistributing space during viewport resizes. These default to the user constraints when not specified. System min must be &gt;= user min, and system max must be &lt;= user max.</p>
<h4 class="d-docsite--header-4">Fixed panels</h4>
<p class="d-docsite--paragraph">Set <code v-pre>:resizable=&quot;false&quot;</code> to fix a panel at its <code v-pre>initial-size</code>. Fixed panels cannot be dragged, and no handle is rendered between a fixed panel and its neighbor. The layout engine subtracts fixed panel widths first, then distributes the remaining space among resizable panels.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel id=&quot;nav&quot; initial-size=&quot;100&quot; :resizable=&quot;false&quot;&gt;
      Navigation (64px, fixed)
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-panel id=&quot;content&quot;&gt;
      Content (fills remaining space)
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable>
    <dt-resizable-panel id="nav" initial-size="100" :resizable="false">
      Navigation (64px, fixed)
    </dt-resizable-panel>
    <dt-resizable-panel id="content">
      Content (fills remaining space)
    </dt-resizable-panel>
  </dt-resizable>
</dt-box>
</code-example>
<h2 id="collapsing-panels" tabindex="-1" class="d-docsite--header-2">Collapsing panels <a class="header-anchor d-link d-docsite--link d-link" href="#collapsing-panels" aria-hidden="true">#</a></h2>
<code-example only-show="demo">
<example-resizable-collapsible />
</code-example>
<p class="d-docsite--paragraph">Mark a panel as <code v-pre>collapsible</code> to let it collapse to zero width. Use the <code v-pre>collapsed</code> prop for the initial state, or call <code v-pre>collapsePanel()</code> from a template ref.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable @panel-collapse=&quot;onPanelCollapse&quot;&gt;
    &lt;dt-resizable-panel
      id=&quot;sidebar&quot;
      initial-size=&quot;25p&quot;
      user-min-size=&quot;300&quot;
      collapsible
      :collapsed=&quot;isSidebarCollapsed&quot;
    &gt;
      Sidebar
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;content&quot;&gt;
      &lt;header&gt;
        &lt;button @click=&quot;isSidebarCollapsed = !isSidebarCollapsed&quot;&gt;
          Toggle sidebar
        &lt;/button&gt;
      &lt;/header&gt;
      Content
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable @panel-collapse="onPanelCollapse">
    <dt-resizable-panel
      id="sidebar"
      initial-size="25p"
      user-min-size="300"
      collapsible
      :collapsed="isSidebarCollapsed"
    >
      Sidebar
    </dt-resizable-panel>
    <dt-resizable-handle />
    <dt-resizable-panel id="content">
      <header>
        <button @click="isSidebarCollapsed = !isSidebarCollapsed">
          Toggle sidebar
        </button>
      </header>
      Content
    </dt-resizable-panel>
  </dt-resizable>
</dt-box>
</code-example>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>script setup<span class="token operator">></span></span>
<span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> ref <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'vue'</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">const</span> isSidebarCollapsed <span class="token operator">=</span> <span class="token function">ref</span><span class="token punctuation">(</span><span class="token boolean">false</span><span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">function</span> <span class="token function">onPanelCollapse</span> <span class="token punctuation">(</span><span class="token parameter">panelId<span class="token punctuation">,</span> collapsed</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token keyword">if</span> <span class="token punctuation">(</span>panelId <span class="token operator">===</span> <span class="token string">'sidebar'</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">    isSidebarCollapsed<span class="token punctuation">.</span>value <span class="token operator">=</span> collapsed<span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"><span class="token operator">&lt;</span><span class="token operator">/</span>script<span class="token operator">></span></span>
<span class="line"></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Listen to <code v-pre>@panel-collapse</code> to keep your local state in sync — the panel can also be collapsed by the system (auto-collapse rules, viewport resize). Always provide a visible control in a sibling panel to restore a collapsed panel.</p>
<h3 id="dynamic-constraints-on-collapse" tabindex="-1" class="d-docsite--header-3">Dynamic constraints on collapse <a class="header-anchor d-link d-docsite--link d-link" href="#dynamic-constraints-on-collapse" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">For layouts where a panel starts hidden (e.g., a detail pane that opens when an item is selected), bind <code v-pre>initial-size</code> to a computed value that changes based on collapsed state:</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable&gt;
    &lt;dt-resizable-panel
      id=&quot;list&quot;
      :initial-size=&quot;isDetailOpen ? &#39;30p&#39; : &#39;100p&#39;&quot;
    &gt;
      List
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel
      id=&quot;detail&quot;
      :initial-size=&quot;isDetailOpen ? &#39;70p&#39; : &#39;0p&#39;&quot;
      collapsible
      :collapsed=&quot;!isDetailOpen&quot;
    &gt;
      Detail
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable>
    <dt-resizable-panel
      id="list"
      :initial-size="isDetailOpen ? '30p' : '100p'"
    >
      List
    </dt-resizable-panel>
    <dt-resizable-handle />
    <dt-resizable-panel
      id="detail"
      :initial-size="isDetailOpen ? '70p' : '0p'"
      collapsible
      :collapsed="!isDetailOpen"
    >
      Detail
    </dt-resizable-panel>
  </dt-resizable>
</dt-box>
</code-example>
<h3 id="auto-collapse-rules" tabindex="-1" class="d-docsite--header-3">Auto-collapse rules <a class="header-anchor d-link d-docsite--link d-link" href="#auto-collapse-rules" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>collapse-rules</code> to define which panels collapse first when space gets tight. Lower priority numbers collapse first.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable
    :collapse-rules=&quot;[
      { panelId: &#39;details&#39;, priority: 1 },
      { panelId: &#39;sidebar&#39;, priority: 2 },
    ]&quot;
  &gt;
    ...
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable
    :collapse-rules="[
      { panelId: 'details', priority: 1 },
      { panelId: 'sidebar', priority: 2 },
    ]"
  >
    ...
  </dt-resizable>
</dt-box>
</code-example>
<h2 id="persisting-panel-sizes" tabindex="-1" class="d-docsite--header-2">Persisting panel sizes <a class="header-anchor d-link d-docsite--link d-link" href="#persisting-panel-sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Add a <code v-pre>storage-key</code> to save panel sizes to localStorage automatically. Users resize once, and the layout restores on their next visit.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable storage-key=&quot;my-layout&quot;&gt;
    ...
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable storage-key="my-layout">
    ...
  </dt-resizable>
</dt-box>
</code-example>
<p class="d-docsite--paragraph">For state management integration (Pinia, Vuex, or an API), implement the <code v-pre>ResizableStorageAdapter</code> interface and pass it via <code v-pre>:storage</code>:</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">const</span> piniaAdapter <span class="token operator">=</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token function">save</span><span class="token punctuation">(</span><span class="token parameter">data</span><span class="token punctuation">)</span> <span class="token punctuation">{</span> layoutStore<span class="token punctuation">.</span><span class="token function">setLayout</span><span class="token punctuation">(</span>data<span class="token punctuation">)</span><span class="token punctuation">;</span> <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token function">load</span><span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token punctuation">{</span> <span class="token keyword">return</span> layoutStore<span class="token punctuation">.</span>layout<span class="token punctuation">;</span> <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token function">clear</span><span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token punctuation">{</span> layoutStore<span class="token punctuation">.</span><span class="token function">clearLayout</span><span class="token punctuation">(</span><span class="token punctuation">)</span><span class="token punctuation">;</span> <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable :storage=&quot;piniaAdapter&quot;&gt;
    ...
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable :storage="piniaAdapter">
    ...
  </dt-resizable>
</dt-box>
</code-example>
<p class="d-docsite--paragraph">When both <code v-pre>storage-key</code> and <code v-pre>:storage</code> are provided, the custom adapter takes precedence.</p>
<h2 id="space-allocation-strategies" tabindex="-1" class="d-docsite--header-2">Space allocation strategies <a class="header-anchor d-link d-docsite--link d-link" href="#space-allocation-strategies" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">When a panel opens or closes, the remaining panels need to redistribute space. The <code v-pre>space-allocation-strategy</code> prop controls how:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong><code v-pre>proportional</code></strong> (default) — All non-collapsed panels give or take space proportionally based on their current size.</li>
<li class="d-docsite--list-element"><strong><code v-pre>preserve-manual</code></strong> — Panels that the user has manually resized keep their exact size. Only panels the user hasn't touched give up space.</li>
</ul>
<h3 id="offset-from-fixed-elements" tabindex="-1" class="d-docsite--header-3">Offset from fixed elements <a class="header-anchor d-link d-docsite--link d-link" href="#offset-from-fixed-elements" aria-hidden="true">#</a></h3>
<code-example only-show="demo">
<example-resizable-offset />
</code-example>
<p class="d-docsite--paragraph">When a fixed or absolutely positioned element (like a toolbar or header) overlaps the resizable area, set <code v-pre>offset-element</code> on <code v-pre>DtResizable</code> to automatically offset all handles and panel content below it.</p>
<code-example only-show="code" source-code='&lt;dt-box block-size=&quot;400&quot; inline-size=&quot;100p&quot;&gt;
  &lt;dt-resizable offset-element=&quot;#doc-demo-toolbar&quot;&gt;
    &lt;dt-box
      id=&quot;doc-demo-toolbar&quot;
      padding-inline=&quot;200&quot;
      surface=&quot;secondary-opaque&quot;
      border-width-block-end=&quot;100&quot;
      block-size=&quot;75&quot;
      class=&quot;d-ps-absolute d-t-0 d-l-0 d-r-0 d-zi-base1 d-d-flex d-ai-center d-jc-center
      &quot;
    &gt;
      Fixed Toolbar (48px)
    &lt;/dt-box&gt;
    &lt;dt-resizable-panel id=&quot;exo-left&quot; initial-size=&quot;50p&quot; class=&quot;d-bgc-success&quot;&gt;
      Left Panel
    &lt;/dt-resizable-panel&gt;
    &lt;dt-resizable-handle /&gt;
    &lt;dt-resizable-panel id=&quot;exo-right&quot; initial-size=&quot;50p&quot; class=&quot;d-bgc-warning-subtle&quot;&gt;
      Right Panel
    &lt;/dt-resizable-panel&gt;
  &lt;/dt-resizable&gt;
&lt;/dt-box&gt;'>
<dt-box block-size="400" inline-size="100p">
  <dt-resizable offset-element="#doc-demo-toolbar">
    <dt-box
      id="doc-demo-toolbar"
      padding-inline="200"
      surface="secondary-opaque"
      border-width-block-end="100"
      block-size="75"
      class="d-ps-absolute d-t-0 d-l-0 d-r-0 d-zi-base1 d-d-flex d-ai-center d-jc-center
      "
    >
      Fixed Toolbar (48px)
    </dt-box>
    <dt-resizable-panel id="exo-left" initial-size="50p" class="d-bgc-success">
      Left Panel
    </dt-resizable-panel>
    <dt-resizable-handle />
    <dt-resizable-panel id="exo-right" initial-size="50p" class="d-bgc-warning-subtle">
      Right Panel
    </dt-resizable-panel>
  </dt-resizable>
</dt-box>
</code-example>
<p class="d-docsite--paragraph">Alternatively, use <code v-pre>offset-amount</code> for an explicit pixel value without measuring an element. If both are provided, <code v-pre>offset-amount</code> takes precedence.</p>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<h3 id="keyboard-navigation" tabindex="-1" class="d-docsite--header-3">Keyboard navigation <a class="header-anchor d-link d-docsite--link d-link" href="#keyboard-navigation" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Each resize handle has <code v-pre>role=&quot;separator&quot;</code> with <code v-pre>aria-orientation</code>, <code v-pre>aria-valuenow</code>, <code v-pre>aria-valuemin</code>, <code v-pre>aria-valuemax</code>, <code v-pre>aria-controls</code>, and <code v-pre>aria-valuetext</code> reflecting the current layout. Handles are always focusable (<code v-pre>tabindex=&quot;0&quot;</code>) and follow the <a href="https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">W3C ARIA separator pattern</a>.</p>
<table class="d-table dialtone-doc-table">
<thead>
<tr>
<th scope="col">Key</th>
<th scope="col">Action</th>
</tr>
</thead>
<tbody>
<tr>
<td>Arrow keys</td>
<td>Resize by 8px</td>
</tr>
<tr>
<td>Shift + Arrow</td>
<td>Resize by 24px</td>
</tr>
<tr>
<td>Ctrl/Cmd + Arrow</td>
<td>Resize by 1px</td>
</tr>
<tr>
<td>Enter</td>
<td>Collapse or expand the adjacent panel (if collapsible)</td>
</tr>
<tr>
<td>Home</td>
<td>Set panel to minimum size</td>
</tr>
<tr>
<td>End</td>
<td>Set panel to maximum size</td>
</tr>
<tr>
<td>R</td>
<td>Reset adjacent panels to initial sizes</td>
</tr>
<tr>
<td>Escape</td>
<td>Remove focus from the handle</td>
</tr>
</tbody>
</table>
<p class="d-docsite--paragraph">Size changes are announced to screen readers via an <code v-pre>aria-live</code> region. All announcement strings are configurable via the <code v-pre>messages</code> prop on <code v-pre>DtResizable</code> for i18n.</p>
<p class="d-docsite--paragraph">Double-clicking a handle resets the two adjacent panels to their initial size proportions.</p>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<h3 id="dtresizable" tabindex="-1" class="d-docsite--header-3">DtResizable <a class="header-anchor d-link d-docsite--link d-link" href="#dtresizable" aria-hidden="true">#</a></h3>
<component-vue-api component-name="resizable" /><h3 id="dtresizablepanel" tabindex="-1" class="d-docsite--header-3">DtResizablePanel <a class="header-anchor d-link d-docsite--link d-link" href="#dtresizablepanel" aria-hidden="true">#</a></h3>
<component-vue-api component-name="resizable_panel" /><h3 id="dtresizablehandle" tabindex="-1" class="d-docsite--header-3">DtResizableHandle <a class="header-anchor d-link d-docsite--link d-link" href="#dtresizablehandle" aria-hidden="true">#</a></h3>
<component-vue-api component-name="resizable_handle" /></div></template>


<script setup>
import ExampleResizable from '@exampleComponents/ExampleResizable.vue';
import ExampleResizableNested from '@exampleComponents/ExampleResizableNested.vue';
import ExampleResizableThreePanel from '@exampleComponents/ExampleResizableThreePanel.vue';
import ExampleResizableVertical from '@exampleComponents/ExampleResizableVertical.vue';
import ExampleResizableCollapsible from '@exampleComponents/ExampleResizableCollapsible.vue';
import ExampleResizableOffset from '@exampleComponents/ExampleResizableOffset.vue';
</script>